import type { Id } from "@teachloop/backend/_generated/dataModel";
import { useRouter } from "expo-router";
import React, { Fragment, useCallback } from "react";
import { Pressable, Text, TouchableOpacity, View } from "react-native";
import ClockIcon from "@/assets/svgs/clock-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import UserIcon from "@/assets/svgs/user-icon";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import type {
  ParentLessonSheetPayload,
  ParentLessonSheetResult,
} from "../sheets/parent-lesson-sheet";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import type { api } from "../utils/convex-api";
import { SheetIds } from "../utils/sheet-ids";
import { useSheet } from "./sheet-provider";

type ParentSessionCardProps = {
  session: NonNullable<
    typeof api.parent.home.queries.weeklySessions._returnType
  >[0] & {
    tutor: {
      id: Id<"tutorProfiles">;
    };
    student: {
      id: Id<"students">;
    };
  };
};

function ParentSessionCard({ session }: ParentSessionCardProps) {
  const fmt = useFormatDate();

  const isSessionInProgress = session.status === "in_progress";
  const isSessionCompleted = session.status === "completed";
  const isSessionNoShow =
    session.status === "no_show_reported_by_student" ||
    session.status === "no_show_reported_by_tutor";

  const { open } = useSheet<ParentLessonSheetPayload, ParentLessonSheetResult>(
    SheetIds.PARENT_LESSON,
  );

  const router = useRouter();

  const handleOpenLessonSheet = useCallback(async () => {
    const result = await open({
      lessonData: session,
    });
    if (result?.cancelLesson) {
      router.push(`/(app)/cancel-lesson?lessonId=${session._id}`);
    }
  }, [open, session, router]);

  const handleJoinIntroCall = useCallback(() => {
    if (session.url) {
      location.href = session.url;
    }
  }, [session.url]);

  const isIntroCall = session.type === "intro_call";
  const canJoinIntroCall = isIntroCall && isSessionInProgress && session.url;

  return (
    <Pressable
      key={session._id}
      className={`p-4 rounded-[17px] gap-3 shadow-hard-3 ${
        isSessionInProgress
          ? "bg-teachLoop-accentPrimary"
          : isSessionCompleted
            ? "bg-teachLoop-accentPrimaryDisabled"
            : "bg-[#FFFDFB]"
      }`}
      style={{
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
        elevation: 2,
      }}
      onPress={handleOpenLessonSheet}
      disabled={false}
    >
      {isSessionNoShow && (
        <View className="mb-2">
          <Text className="font-geologica-medium font-medium text-sm text-red-600">
            No Show
          </Text>
        </View>
      )}
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-2">
          <Text
            className={`font-geologica-medium font-medium text-base ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } leading-[1.5]`}
          >
            {session.subject}
          </Text>
        </View>

        {canJoinIntroCall ? (
          <Button
            className="bg-white"
            size="sm"
            icon
            onPress={handleJoinIntroCall}
          >
            <View className="w-[9px] h-[9px] bg-green-400 rounded-full items-center justify-center" />
            <Button.Text intent="primary" size="sm">
              Join intro call
            </Button.Text>
          </Button>
        ) : (
          <Text
            className={`font-geologica-medium font-medium text-base ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } leading-[1.2]`}
          >
            {fmt(session.startTime, "do MMMM")}
          </Text>
        )}
      </View>

      <View className="flex-row items-center justify-between">
        <TouchableOpacity
          onPress={() =>
            router.push(
              `/(app)/home/find-tutor/${session.tutor.id}?childId=${session.student.id}`,
            )
          }
          className="flex-row items-center gap-2"
        >
          <Avatar size="sm" className="h-6 w-6">
            <Avatar.Content
              label={session.tutor.firstName ?? ""}
              source={
                session.tutor.imageUrl
                  ? { uri: session.tutor.imageUrl }
                  : undefined
              }
              labelSize="sm"
              size="sm"
            />
          </Avatar>
          <Text
            className={`font-geologica-regular font-normal ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } text-sm leading-[1.5]`}
          >
            {session.tutor.firstName}
          </Text>
        </TouchableOpacity>

        <View className="flex-row items-center gap-[6px]">
          {session.deliveryMethod === "in_person" ? (
            <UserIcon
              className="size-4"
              pathClassName={
                isSessionInProgress ? "stroke-white" : "stroke-gray-800"
              }
            />
          ) : (
            <RemoteIcon
              className="size-4"
              pathClassName={
                isSessionInProgress ? "stroke-white" : "stroke-gray-800"
              }
            />
          )}
          <Text
            className={`font-geologica-regular font-normal ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } text-sm leading-[1.5]`}
          >
            {session.deliveryMethod === "in_person" ? "In person" : "Remote"}
          </Text>
        </View>

        <View className="flex-row items-center gap-2">
          <ClockIcon
            className="size-4"
            pathClassName={
              isSessionInProgress ? "stroke-white" : "stroke-gray-800"
            }
          />
          <Text
            className={`font-geologica-regular font-normal ${
              isSessionInProgress ? "text-white" : "text-gray-800"
            } text-sm leading-[1.5]`}
          >
            {fmt(session.startTime, "HH:mm")} - {fmt(session.endTime, "HH:mm")}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

export default ParentSessionCard;
